import { InfoCircleOutlined, LogoutOutlined } from "@ant-design/icons"
import { Alert, Button, Form, Segmented, Skeleton, Switch } from "antd"
import React, { useEffect, useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"

import { CardComponent } from "../../components/Card/CardComponent.tsx"
import { TextInput } from "../../components/Input/TextInput.tsx"
import { useAppDispatch } from "../../hooks/use-app-dispatch.ts"
import { useAppSelector } from "../../hooks/use-app-selector.ts"
import { request } from "../../services/api.ts"
import { PortalPageContainer } from "./components/PortalPageContainer.tsx"
import { PortalPageHeader } from "./components/PortalPageHeader.tsx"
import { logout as logoutThunk } from "../LoginPage/redux/auth.thunks.ts"
import {
  fetchPortalProfile,
  updateProfileThunk
} from "./redux/portal.thunks.ts"
import type { PortalCustomerProfile } from "./types/portal.types.ts"

type Tab = "personal" | "notifications" | "account"

const EDITABLE_FIELDS: (keyof PortalCustomerProfile)[] = [
  "first_name",
  "last_name",
  "phone",
  "alternate_phone",
  "address",
  "address_2",
  "city",
  "state",
  "zip_code",
  "preferred_contact_method"
]

const asFormValues = (profile: PortalCustomerProfile | null) => {
  const values: Record<string, string> = {}
  EDITABLE_FIELDS.forEach((field) => {
    values[field] = profile?.[field] ? String(profile[field]) : ""
  })
  return values
}

// The form always carries every field (antd re-submits the whole model on
// `onFinish`), but the wire payload must carry only what actually changed —
// sending an untouched field's coerced-to-"" display value would fan out as
// an explicit clear to every linked GC's Customer record, silently blanking
// a field the customer never touched. Compared against the loaded profile's
// own display values, not the raw `profile` object, since that's the same
// coercion the field started from (`asFormValues`) and empty-vs-null must
// not read as a change.
export const computeChangedProfileFields = (
  values: Record<string, string>,
  profile: PortalCustomerProfile
): Record<string, string> => {
  const original = asFormValues(profile)
  return Object.fromEntries(
    Object.entries(values).filter(([field, value]) => value !== original[field])
  )
}

function PersonalDetailsTab() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const [form] = Form.useForm()
  const [dirty, setDirty] = useState(false)
  const [saveState, setSaveState] = useState<"idle" | "success">("idle")

  const profile = useAppSelector((state) => state.portal.profile)
  const fetching = useAppSelector((state) => state.portal.profileFetching)
  const saving = useAppSelector((state) => state.portal.profileSaving)
  const conflict = useAppSelector((state) => state.portal.profileConflict)
  const error = useAppSelector((state) => state.portal.profileError)

  useEffect(() => {
    void dispatch(fetchPortalProfile())
  }, [dispatch])

  useEffect(() => {
    if (profile) form.setFieldsValue(asFormValues(profile))
  }, [profile, form])

  useEffect(() => {
    const handler = (e: BeforeUnloadEvent) => {
      if (dirty) e.preventDefault()
    }
    window.addEventListener("beforeunload", handler)
    return () => window.removeEventListener("beforeunload", handler)
  }, [dirty])

  const handleSave = async (values: Record<string, string>) => {
    if (!profile) return
    setSaveState("idle")
    const changed = computeChangedProfileFields(values, profile)
    const result = await dispatch(
      updateProfileThunk({
        ...changed,
        expected_updated_at: profile.updated
      })
    )
    if (updateProfileThunk.fulfilled.match(result)) {
      setDirty(false)
      setSaveState("success")
    }
  }

  const handleCancel = () => {
    form.setFieldsValue(asFormValues(profile))
    setDirty(false)
  }

  const handleReload = () => {
    void dispatch(fetchPortalProfile())
  }

  if (fetching && !profile) {
    return <Skeleton active paragraph={{ rows: 6 }} />
  }

  if (error) {
    return (
      <Alert
        type="error"
        showIcon
        message={t("portal.settings.loadError")}
        action={
          <Button size="small" onClick={handleReload}>
            {t("portal.home.retry")}
          </Button>
        }
      />
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="flex items-center gap-1.5 text-sm text-gray-500">
        <InfoCircleOutlined />
        {t("portal.settings.sharedWithContractor")}
      </p>

      {!!profile?.linked_relationships.length && (
        <div>
          <p className="mb-1 text-xs font-medium uppercase text-gray-400">
            {t("portal.settings.connectedCompanies")}
          </p>
          <div className="flex flex-wrap gap-1.5">
            {profile.linked_relationships.map((relationship) => (
              <span
                key={relationship.id}
                className="rounded-full bg-gray-100 px-3 py-1 text-sm text-gray-700"
              >
                {relationship.gcc_name}
              </span>
            ))}
          </div>
        </div>
      )}

      {conflict && (
        <Alert
          type="warning"
          showIcon
          message={t("portal.settings.conflict")}
          action={
            <Button size="small" onClick={handleReload}>
              {t("portal.settings.reload")}
            </Button>
          }
        />
      )}

      {saveState === "success" && !dirty && (
        <Alert
          type="success"
          showIcon
          message={t("portal.settings.saved")}
          closable
        />
      )}

      <Form
        form={form}
        layout="vertical"
        onFinish={(values) => void handleSave(values)}
        onValuesChange={() => {
          setDirty(true)
          setSaveState("idle")
        }}
      >
        <div className="grid grid-cols-1 gap-x-4 sm:grid-cols-2">
          <Form.Item
            name="first_name"
            label={t("portal.settings.firstName")}
            rules={[{ required: true, message: t("portal.settings.required") }]}
          >
            <TextInput />
          </Form.Item>
          <Form.Item
            name="last_name"
            label={t("portal.settings.lastName")}
            rules={[{ required: true, message: t("portal.settings.required") }]}
          >
            <TextInput />
          </Form.Item>
          <Form.Item name="phone" label={t("portal.settings.phone")}>
            <TextInput disableAutoCapitalize />
          </Form.Item>
          <Form.Item
            name="alternate_phone"
            label={t("portal.settings.phoneAlt")}
          >
            <TextInput disableAutoCapitalize />
          </Form.Item>
          <Form.Item label={t("portal.settings.contactEmail")}>
            <TextInput
              value={profile?.email ?? ""}
              disabled
              disableAutoCapitalize
            />
          </Form.Item>
          <Form.Item
            name="preferred_contact_method"
            label={t("portal.settings.preferredContact")}
          >
            <TextInput
              placeholder="email / phone / sms"
              disableAutoCapitalize
            />
          </Form.Item>
          <Form.Item
            name="address"
            label={t("portal.settings.address")}
            className="sm:col-span-2"
          >
            <TextInput />
          </Form.Item>
          <Form.Item name="address_2" label={t("portal.settings.addressLine2")}>
            <TextInput />
          </Form.Item>
          <Form.Item name="city" label={t("portal.settings.city")}>
            <TextInput />
          </Form.Item>
          <Form.Item name="state" label={t("portal.settings.state")}>
            <TextInput />
          </Form.Item>
          <Form.Item name="zip_code" label={t("portal.settings.zipCode")}>
            <TextInput disableAutoCapitalize />
          </Form.Item>
        </div>

        <div className="mt-2 flex gap-2">
          <Button
            type="primary"
            htmlType="submit"
            loading={saving}
            disabled={!dirty}
          >
            {t("portal.settings.saveChanges")}
          </Button>
          <Button onClick={handleCancel} disabled={!dirty || saving}>
            {t("portal.settings.cancel")}
          </Button>
        </div>
      </Form>
    </div>
  )
}

function NotificationsTab() {
  const { t } = useTranslation()
  // No backend preference model reaches a customer recipient yet (every
  // notification recipient-building call site in ezbuild_back structurally
  // excludes the CUSTOMER role — see this session's delivery notes). These
  // toggles are shown so the section isn't blank, but are visually marked
  // as not yet affecting delivery rather than silently doing nothing.
  const categories = [
    "portal.settings.notifCategoryMessages",
    "portal.settings.notifCategoryUpdates",
    "portal.settings.notifCategoryRequests",
    "portal.settings.notifCategoryInvoices"
  ]

  return (
    <div className="flex flex-col gap-4">
      <Alert
        type="info"
        showIcon
        message={t("portal.settings.notifNotWiredYet")}
      />
      <p className="text-sm text-gray-500">
        {t("portal.settings.notifEssential")}
      </p>
      <div className="flex flex-col divide-y divide-gray-100">
        {categories.map((key) => (
          <div key={key} className="flex items-center justify-between py-3">
            <div>
              <p className="text-sm font-medium text-gray-800">{t(key)}</p>
            </div>
            <div className="flex items-center gap-4">
              <span className="flex items-center gap-1 text-xs text-gray-400">
                {t("portal.settings.notifEmail")}
                <Switch size="small" disabled defaultChecked />
              </span>
              <span className="flex items-center gap-1 text-xs text-gray-400">
                {t("portal.settings.notifInApp")}
                <Switch size="small" disabled defaultChecked />
              </span>
            </div>
          </div>
        ))}
      </div>
    </div>
  )
}

function AccountSecurityTab() {
  const { t } = useTranslation()
  const dispatch = useAppDispatch()
  const navigate = useNavigate()
  const userInfo = useAppSelector((state) => state.auth.userInfo)
  const [sendingReset, setSendingReset] = useState(false)
  const [resetSent, setResetSent] = useState(false)

  const handlePasswordReset = async () => {
    if (!userInfo?.email) return
    setSendingReset(true)
    await request.api.post("auth/forgot-password/", { email: userInfo.email })
    setSendingReset(false)
    setResetSent(true)
  }

  return (
    <div className="flex flex-col gap-4">
      <div>
        <p className="text-xs uppercase text-gray-400">
          {t("portal.settings.signInEmail")}
        </p>
        <p className="text-sm text-gray-800">{userInfo?.email}</p>
        <p className="mt-1 text-xs text-gray-400">
          {t("portal.settings.emailChangeNote")}
        </p>
      </div>

      <div className="border-t border-gray-100 pt-4">
        <p className="text-xs uppercase text-gray-400">
          {t("portal.settings.password")}
        </p>
        {resetSent ? (
          <Alert
            className="mt-2"
            type="success"
            showIcon
            message={t("portal.settings.resetSent")}
          />
        ) : (
          <Button
            className="mt-2"
            loading={sendingReset}
            onClick={() => void handlePasswordReset()}
          >
            {t("portal.settings.sendResetLink")}
          </Button>
        )}
      </div>

      <div className="border-t border-gray-100 pt-4">
        <Button
          icon={<LogoutOutlined />}
          onClick={() => void dispatch(logoutThunk({ navigate }))}
        >
          {t("portal.nav.signOut")}
        </Button>
      </div>
    </div>
  )
}

function PortalSettingsPage() {
  const { t } = useTranslation()
  const [tab, setTab] = useState<Tab>("personal")

  return (
    // Left-aligned at the same gutter every other portal page starts from
    // (`mx-0` overrides PortalPageContainer's default centering) and capped
    // at a comfortable reading width — never centered as an island inside
    // the full-width shell.
    <PortalPageContainer className="mx-0 max-w-4xl">
      <PortalPageHeader
        title={t("portal.settings.title")}
        subtitle={t("portal.settings.subtitle")}
      />

      <div className="mb-5 overflow-x-auto">
        <Segmented
          value={tab}
          onChange={(value) => setTab(value as Tab)}
          options={[
            { label: t("portal.settings.tabPersonal"), value: "personal" },
            {
              label: t("portal.settings.tabNotifications"),
              value: "notifications"
            },
            { label: t("portal.settings.tabAccount"), value: "account" }
          ]}
        />
      </div>

      <CardComponent>
        {tab === "personal" && <PersonalDetailsTab />}
        {tab === "notifications" && <NotificationsTab />}
        {tab === "account" && <AccountSecurityTab />}
      </CardComponent>
    </PortalPageContainer>
  )
}

export default PortalSettingsPage
